<template>
  <div>
    <v-row class="mt-10 pl-3 pr-7" justify="space-between">
      <div>
        <p class="title">Informações do Plano</p>
      </div>
      <div>
        <v-btn class="primary--text" color="muted-foreground" v-if="isDisabledFields" large @click="onClickEdit()">
          <v-icon class="mr-2">
            fas fa-edit
          </v-icon>
          Editar
        </v-btn>
        <v-btn :disabled="loadingFinancialGroups || loadingContracts || loadingSubContracts || loadingEligibilities || loadingPlans || loadingUnits" color="primary" v-else large @click="saveDataInSessionStorage()">
          <v-icon class="mr-2" color="foreground-accent">
            fas fa-save
          </v-icon>
          Salvar
        </v-btn>
      </div>
    </v-row>
    <v-form ref="form" v-model="valid" lazy-validation>
      <v-row class="mt-10 pr-4">
        <v-col cols="12" md="6" lg="3">
          <label class="label">Contrato / Apólice</label>
          <v-autocomplete
            v-model="plan.contract"
            :items="contracts"
            item-value="id"
            item-text="text"
            :class="verifyFieldInArray('policy') ? 'alterationColorText alterationColorBorder mt-2' : 'mt-2'"
            placeholder="Número do contrato"
            outlined
            color="primary"
            @input="onInputContract"
            :rules="isReuseMovement ? [rule.required] : []"
            :loading="loadingContracts"
            :disabled="isDisabledFields || (!isDisabledFields && !isReuseMovement) || mountedFinish"
          />
        </v-col>
        <v-col cols="12" md="6" lg="3">
          <label class="label">Sub-Contrato</label>
          <v-autocomplete
            v-model="plan.subcontract"
            :items="subContracts"
            item-text="text"
            item-value="id"
            :class="verifyFieldInArray('subContract') ? 'alterationColorText alterationColorBorder mt-2' : 'mt-2'"
            placeholder="Número do sub-contrato"
            outlined
            color="primary"
            :rules="[rule.required]"
            @input="onInputSubContract"
            :loading="loadingSubContracts || isDisabledSubContracts"
            :disabled="isDisabledFields || mountedFinish || loadingSubContracts || isDisabledSubContracts"
            :success-messages="isReuseMovement ? '' : subContractMessage"
          />
        </v-col>
        <v-col cols="12" md="6" lg="3">
          <label class="label">Unidade</label>
          <v-row no-gutters>
            <v-col class="flex-grow-0 flex-shrink-0">
              <v-tooltip top color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <v-autocomplete
                      :class="verifyFieldInArray('unitId') ? 'alterationColorText alterationColorBorder mt-2 rounded-r-0' : 'mt-2 rounded-r-0'"
                      v-model="plan.unit"
                      :items="plan.units"
                      @change="setUnit(plan, $event)"
                      item-text="code"
                      item-value="id"
                      placeholder="Código"
                      style="min-width: 120px;"
                      maxlength="70"
                      outlined
                      color="primary"
                      :disabled="isDisabledUnitField"
                      :loading="loadingUnits"
                      validate-on-blur
                      return-object
                      clearable
                    />
                  </div>
                </template>
                <span>{{ formatUnityTooltip(plan.unit, plan.unitCode, 'code') }}</span>
              </v-tooltip>
            </v-col>
            <v-col class="flex-grow-1 flex-shrink-1">
              <v-tooltip top color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <v-text-field
                      :class="verifyFieldInArray('unitId') ? 'alterationColorText alterationColorBorder mt-2 rounded-l-0' : 'mt-2 rounded-l-0'"
                      :value="plan && plan.unit ? plan.unit.description : ''"
                      placeholder="Descrição"
                      maxlength="70"
                      outlined
                      color="primary"
                      disabled
                    />
                  </div>
                </template>
                <span>{{ formatUnityTooltip(plan.unit, plan.unitDescription, 'description') }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" md="6" lg="3">
          <label class="label">Elegibilidade</label>
          <v-autocomplete
            :items="eligibilities"
            :class="verifyFieldInArray('contractEligibilityId') ? 'alterationColorText alterationColorBorder mt-2' : 'mt-2'"
            item-text="elegibility_id.name"
            item-value="id"
            v-model="plan.eligibility"
            :disabled="isDisabledFields || mountedFinish || loadingEligibilities || isDisabledEligibilites"
            outlined
            clearable
            placeholder="Selecione"
            color="primary"
            @input="onInputEligibility"
            :loading="loadingEligibilities || isDisabledEligibilites"
            :success-messages="isReuseMovement ? '' : eligibilityMessage"
          />
        </v-col>
        <v-col cols="12" md="6" lg="3">
          <label class="label">Plano</label>
          <v-autocomplete
            v-model="plan.plan"
            :items="plans"
            item-text="name"
            item-value="id"
            clearable
            :class="verifyFieldInArray('plan') ? 'alterationColorText alterationColorBorder mt-2' : 'mt-2'"
            :disabled="disabledPlanInput"
            placeholder="Selecione"
            outlined
            color="primary"
            :loading="loadingPlans || isDisabledPlans"
            :rules="[rule.required]"
            @input="onInputPlan"
            :success-messages="isReuseMovement ? '' : previousPlanName"
          />
        </v-col>
        <v-col cols="12" md="6" lg="3">
          <label class="label d-flex justify-space-between">
            <span>Início de Vigência</span>
            <v-tooltip
              v-if="areDifferentValidityDates"
              bottom
            >
              <template
                v-slot:activator="{ on, attrs }"
              >
                <v-icon
                  v-on="on"
                  v-bind="attrs"
                  :disabled="isDisabledFields"
                  @click="setPreviewsValidityDate"
                  size="15"
                  color="primary"
                >
                  fas fa-undo
                </v-icon>
              </template>

              <span>
                Voltar data de Início de Vigência salva
              </span>
            </v-tooltip>
          </label>
          <div class="d-flex flex-column">
            <v-menu
              v-model="validityDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              left
              min-width="auto">
              <template
                v-slot:activator="{ on, attrs }">
                <v-text-field
                  :class="verifyFieldInArray('startDate') ? 'alterationColorText alterationColorBorder mt-2' : 'mt-2'"
                  outlined
                  :disabled="isDisabledFields || mountedFinish"
                  v-model="plan.validityDate"
                  append-icon="fas fa-calendar-alt"
                  placeholder="DD/MM/YYYY"
                  v-mask="'##/##/####'"
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  readonly
                  clearable
                  :loading="loadingValidityDate"
                  @click="getAllowedDates(plan)"
                  @click:clear="clearScheduledReleaseDate()"
                  @input="saveValidatyDate(plan.validityDate)"
                  :rules="plan.validityDate ? [rule.requiredDate, rule.isAfterSpecificYear, rule.isDateValid, rule.required] : [rule.required]"
                  :success-messages="isReuseMovement ? '' : validityDateMessage"
                />
              </template>
              <AllowedDatePicker
                @date="updateValidityDate($event, plan)"
                :allowedDates="allowedDates"
              />
            </v-menu>
          </div>
        </v-col>
        <v-col
          v-if="shouldRenderMovementDeadlineField(scheduledReleaseDate, showScheduledReleaseDate, loadingScheduledReleaseDate) &&
            (this.movementType !== 'UPDATE' ||
              (this.plan.plan !== Number(this.holderAlterationSessionStorage.planId) || Number(this.holderAlterationSessionStorage.subcontractId) !== this.plan.subcontract)
            )"
          :key="getMovementDeadlineFieldRenderKey(
            plan.movementDeadlineConfig,
            scheduledReleaseDate,
            loadingScheduledReleaseDate,
            'plan-scheduled-release-col'
          )"
          cols="12"
          md="6"
          lg="3">
          <label class="label">Data Agendada de Liberação</label>
          <v-tooltip
            :key="getMovementDeadlineFieldRenderKey(
              plan.movementDeadlineConfig,
              scheduledReleaseDate,
              loadingScheduledReleaseDate,
              'plan-scheduled-release-tooltip'
            )"
            top>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <v-text-field
                    :key="getMovementDeadlineFieldRenderKey(
                      plan.movementDeadlineConfig,
                      scheduledReleaseDate,
                      loadingScheduledReleaseDate,
                      'plan-scheduled-release'
                    )"
                    class='mt-2'
                    outlined
                    :value="scheduledReleaseDate"
                    color="primary"
                    readonly
                    append-icon="fas fa-calendar-alt"
                    :loading="loadingScheduledReleaseDate"
                  />
              </div>
            </template>
            <span>{{ getMovementDeadlineTooltip(plan.validityDate, plan.movementDeadlineConfig) }}</span>
          </v-tooltip>
        </v-col>
        <v-col cols="12" md="6" lg="3">
          <label class="label">Motivo da alteração</label>
          <v-autocomplete
            v-model="plan.planChangeReasonCode"
            :items="planChangeReasons"
            :disabled="isDisabledFields"
            placeholder="Selecione"
            item-text="name"
            item-value="code"
            outlined
            color="primary"
            clearable
            :class="verifyFieldInArray('planChangeReasonCode') ? 'alterationColorText alterationColorBorder mt-2' : 'mt-2'"
          />
        </v-col>
        <v-col cols="12" md="6" lg="3">
            <label class="label">Código Familiar</label>
            <v-text-field
              outlined
              v-model="plan.familyCode"
              v-mask="`${ formatter.maskLimiter(10) }`"
              color="primary"
              placeholder="Informe código familiar"
              :disabled="isDisabledFields || isDisabledFamilyCode"
              :class="verifyFieldInArray('familyCode') ? 'alterationColorText alterationColorBorder mt-2' : 'mt-2'"
            />
          </v-col>
          <v-col cols="12" md="6" lg="3">
            <label class="label">Sequencial</label>
            <v-text-field
              outlined
              v-model="plan.familyCodeSequential"
              v-mask="`${ formatter.maskLimiter(5) }`"
              color="primary"
              placeholder="Informe código sequencial"
              :disabled="isDisabledFields || isDisabledFamilyCodeSequential"
              :class="verifyFieldInArray('familyCodeSeq') ? 'alterationColorText alterationColorBorder mt-2' : 'mt-2'"
            />
          </v-col>
      </v-row>
    </v-form>
    <SnackbarCustomize ref="SnackbarCustomize" />
  </div>
</template>
<script>
import Formatters from '@/shared/formatters/formatters';
import Rules from '@/shared/validators/formRules';
import lodash from 'lodash';
import PlanChangeReasonServiceSyncBase from '../../../services-http/contract/PlanChangeReasonServiceSyncBase';
import ChangedFieldsMixin from '@/shared/mixins/highlightedChangedFields/changedFieldsMixin';
import FinancialGroupsMixin from '@/shared/mixins/sdi/financialGroupsMixin';
import ContractsMixin from '@/shared/mixins/sdi/contractsMixin';
import EligibilitiesMixin from '@/shared/mixins/sdi/eligibilitiesMixin';
import PlansMixin from '@/shared/mixins/sdi/plansMixin';
import MovementRecordsMixin from '@/shared/mixins/movementRecords/movementRecordsMixin';
import AllowedDatePicker from '@/components/AllowedDatePicker/AllowedDatePicker.vue';
import CarrierService from '@/services-http/contract/CarrierService';
import ContractUnitService from '@/services-http/contract/ContractUnitService';
import moment from 'moment';
import ReuseMovementMixin from '@/shared/mixins/movementRecords/reuse/ReuseMovementMixin';
import SnackbarCustomize from '@/components/CustomAlerts/SnackbarCustomize.vue';
import UnityMixin from '@/shared/mixins/unityMixin/unitymixin';

const createEmptyUnit = () => ({
  id: null,
  code: null,
  description: null,
});

export default {
  name: 'PlanInformation',

  components: {
    AllowedDatePicker,
    SnackbarCustomize,
  },

  data: () => ({
    moment: moment,
    valid: true,
    isDisabledFields: true,
    plan: {
      contract: null,
      subcontract: null,
      eligibility: null,
      financialGroupId: null,
      plan: null,
      previousPlan: null,
      planChangeReasonCode: null,
      validityDate: null,
      familyCode: null,
      familyCodeSequential: null,
      unitId: null,
      unitCode: null,
      unitDescription: null,
      units: [],
      unit: createEmptyUnit(),
      capacityCode: null,
      capacityDescription: null,
      department: null,
      movementDeadlineConfig: null,
    },
    currentPlanInfo: {},
    financialGroups: [],
    contracts: [],
    subContracts: [],
    eligibilities: [],
    eligibilitiesIds: [],
    plans: [],
    planChangeReasons: [],
    allowedDates: [],
    loadingFinancialGroups: false,
    loadingContracts: false,
    loadingSubContracts: false,
    loadingEligibilities: false,
    loadingPlans: false,
    loadingValidityDate: false,
    loadingUnits: false,
    isCriticizedCarrier: false,
    isCriticizedBroker: false,
    isEdit: false,
    isDisabledSubContracts: false,
    isDisabledEligibilites: false,
    isDisabledPlans: false,
    isDisabledFamilyCode: false,
    isDisabledFamilyCodeSequential: false,
    isMounted: true,
    mountedFinish: true,

    movementType: null,
    validityDateMenu: null,
    validityDatePicker: null,
    carrierService: null,
    subContractNumber: null,
    previousSubContract: null,
    previousSubContractNumber: null,
    previousSubContractName: '',
    previousEligibilityName: '',
    previousValidityDate: '',
    previousPlanName: '',
    isFreeMovement: null,
    financialGroupData: null,
    contractData: null,
    holderAdmissionDate: null,
    scheduledReleaseMovementType: 'change',
    contractUnitService: null,
    holderAlterationSessionStorage: {},
    dependentAlterationSessionStorage: {},
    movementRecord: {},
    movementRecordService: null,
    scheduledReleaseDate: null,
    showScheduledReleaseDate: false,
    loadingScheduledReleaseDate: false,
  }),

  mixins: [
    FinancialGroupsMixin,
    ContractsMixin,
    EligibilitiesMixin,
    PlansMixin,
    ChangedFieldsMixin,
    ReuseMovementMixin,
    MovementRecordsMixin,
    UnityMixin,
  ],

  async mounted() {
    this.mountedFinish = true;
    this.verifyTypeOperationForMovement();
    await this.onClickEdit();
    this.loadPlanChangeReasons();
    await this.loadingContractedPlan();
    this.mountedFinish = false;
  },

  props: {
    admissionDateHolder: { type: String },
    changedFields: { type: Array },
  },

  methods: {
    async onInputContract() {
      this.resetUnit();

      if (this.isReuseMovement) {
        this.subContracts = [];
        this.eligibilities = [];
        this.plans = [];
        this.plan.subcontract = '';
        this.plan.eligibility = '';
        this.plan.plan = '';
        this.plan.validityDate = '';
      }

      this.setDaysToReleaseAppointment();

      if (this.plan.contract) {
        const planContract = this.contracts.find((contract) => contract.id === this.plan.contract)

        this.plan.carrierId = planContract ? planContract.carrier_id.id : null;
      }

      this.$emit("observerContractedPlan", this.plan)

      await this.loadSubContracts();

      if (this.isReuseMovement) {
        await this.validateAndAssignReusePlanFields();
      }

      if (this.plan.subcontract) {
        await this.loadUnits();
      }
    },
    async onInputSubContract() {
      this.resetUnit();

      if (this.isReuseMovement) {
        this.eligibilities = [];
        this.plans = [];
        this.plan.plan = '';
      }
      this.plan.eligibility = '';
      this.setDaysToReleaseAppointment();
      this.$emit("observerContractedPlan", this.plan)
      await this.loadEligibilities();
      await this.loadUnits();
    },
    async onInputEligibility(eligibilityId) {
      if (eligibilityId) {
        this.plan.plan = '';
      } else {
        this.plan.plan = this.currentPlanInfo.plan;
      }
      await this.loadPlans();
      this.$emit("observerContractedPlan", this.plan)
      this.setDaysToReleaseAppointment();
    },
    onInputPlan() {
      this.observableChangePlan();
      this.resetDates();
      this.setDaysToReleaseAppointment();
      this.$emit("observerContractedPlan", this.plan)
    },
    async getAllowedDates(item) {
      await this.carrierService.GetAllowedDates([item.contract], this.movementType).then(response => {
        if (response.status === 200) {
          this.allowedDates = response.data;
        }
      })
    },
    async updateValidityDate(value, plan) {
      if (Boolean(value)) {
        plan.validityDate = moment(value, 'YYYY-MM-DD').format('DD/MM/YYYY');
        this.validityDateMenu = false;
        await this.$nextTick();
        await this.validateShowScheduledReleaseDate();
      }
    },
    verifyTypeOperationForMovement() {
      if (sessionStorage.getItem('isCriticizedBroker')) {
        this.isCriticizedBroker = true;
      }
      if (sessionStorage.getItem('isCriticizedCarrier')) {
        this.isCriticizedCarrier = true;
      }
      if (sessionStorage.getItem('isEdit')) {
        this.isEdit = true;
      }
    },
    setUnit(plan, event) {
      const selectedUnit = event || createEmptyUnit();

      plan.unit = selectedUnit;
      plan.unitId = selectedUnit.id || null;
      plan.unitCode = selectedUnit.code || null;
      plan.unitDescription = selectedUnit.description || null;
    },
    async getUnitById(unitId) {
      if (!unitId) return null;

      const response = await this.contractUnitService.GetUnitById(unitId);
      const unit = response && response.data
        ? (Array.isArray(response.data) ? response.data[0] : response.data)
        : null;

      if (unit) {
        this.setUnit(this.plan, unit);
      }

      return unit;
    },
    async getUnitByContractIdAndSubcontractId(contractId, subcontractId) {
      this.plan.units = [];

      if (!contractId || !subcontractId) return;

      const response = await this.contractUnitService.GetUnitByContractIdAndSubcontractId(
        contractId,
        subcontractId
      );

      if (response && response.data) {
        this.plan.units = response.data;
      }
    },
    async loadUnits() {
      if (!this.plan.contract || !this.plan.subcontract) {
        this.plan.units = [];
        return;
      }

      this.loadingUnits = true;

      try {
        await this.getUnitByContractIdAndSubcontractId(this.plan.contract, this.plan.subcontract);
        await this.syncSelectedUnit();
      } finally {
        this.loadingUnits = false;
      }
    },
    async syncSelectedUnit() {
      if (!this.plan.unitId) return;

      const selectedUnit = this.plan.units.find(
        (unit) => String(unit.id) === String(this.plan.unitId)
      );

      if (selectedUnit) {
        this.setUnit(this.plan, selectedUnit);
        return;
      }

      if (!this.plan.unitCode || !this.plan.unitDescription) {
        await this.getUnitById(this.plan.unitId);
      }
    },
    resetUnit() {
      this.plan.units = [];
      this.setUnit(this.plan, null);
    },
    async loadSessionStorage() {
      if (this.$route.query.freeMovement && (this.$route.query.freeMovement === 'true' || this.$route.query.freeMovement === true)) {
        this.isFreeMovement = true;
        await this.loadSessionStorageForFreeMovement();
      } else {
        await this.loadInformations();
      }
    },
    async loadInformations() {
      if (sessionStorage.getItem('holderAlteration')) {
        await this.setHolderAlteration();
      } else if (sessionStorage.getItem('dependentAlteration')) {
        this.setDependentAlteration();
      } else if (sessionStorage.getItem('movementRecord')) {
        this.isCriticizedCarrier = true;
        this.mapBeneficiaryMovementRecord();
      }
    },
    async setHolderAlteration() {
      this.holderAlterationSessionStorage = JSON.parse(sessionStorage.getItem('holderAlteration'));
      if (this.holderAlterationSessionStorage) {
        const dateHolderFormat = this.holderAlterationSessionStorage.startDate ? this.formatter.formatDate(this.holderAlterationSessionStorage.startDate) : null;

        this.plan = {
          financialGroupId: this.holderAlterationSessionStorage.financialGroupId ? Number(this.holderAlterationSessionStorage.financialGroupId) : null,
          contract: !this.isReuseMovement ? this.holderAlterationSessionStorage.contractId ? Number(this.holderAlterationSessionStorage.contractId) : null : null,
          subcontract: !this.isReuseMovement ? this.holderAlterationSessionStorage.subcontractId ? Number(this.holderAlterationSessionStorage.subcontractId) : null : null,
          eligibility: !this.isReuseMovement ? this.holderAlterationSessionStorage.eligibilityId ? Number(this.holderAlterationSessionStorage.eligibilityId) : null : null,
          plan: !this.isReuseMovement ? this.holderAlterationSessionStorage.planId ? Number(this.holderAlterationSessionStorage.planId) : null : null,
          validityDate: !this.isReuseMovement ? dateHolderFormat || null : null,
          familyCode: this.holderAlterationSessionStorage.memberFamilyCode,
          familyCodeSequential: this.holderAlterationSessionStorage.memberFamilyCodeSeq,
          unitId: this.holderAlterationSessionStorage.unitId || this.holderAlterationSessionStorage.unit || null,
          unitCode: this.holderAlterationSessionStorage.unitCode || null,
          unitDescription: this.holderAlterationSessionStorage.unitDescription || null,
          units: [],
          unit: {
            id: this.holderAlterationSessionStorage.unitId || this.holderAlterationSessionStorage.unit || null,
            code: this.holderAlterationSessionStorage.unitCode || null,
            description: this.holderAlterationSessionStorage.unitDescription || null,
          },
          movementDeadlineConfig: null,
        };

        this.isDisabledFamilyCode = this.plan.familyCode !== null;
        this.isDisabledFamilyCodeSequential = this.plan.familyCodeSequential !== null;
        this.currentPlanInfo = lodash.cloneDeep(this.plan);
      }
    },
    setDependentAlteration() {
      this.dependentAlterationSessionStorage = JSON.parse(sessionStorage.getItem('dependentAlteration'));
      if (this.dependentAlterationSessionStorage) {
        const dateDependentFormat = this.dependentAlterationSessionStorage.startDate ? this.formatter.formatDate(this.dependentAlterationSessionStorage.startDate) : null;
        this.plan = {
          financialGroupId: this.dependentAlterationSessionStorage.financialGroupId ? Number(this.dependentAlterationSessionStorage.financialGroupId) : null,
          contract: !this.isReuseMovement ? this.dependentAlterationSessionStorage.contractId ? Number(this.dependentAlterationSessionStorage.contractId) : null : null,
          subcontract: !this.isReuseMovement ? this.dependentAlterationSessionStorage.subcontractId ? Number(this.dependentAlterationSessionStorage.subcontractId) : null : null,
          eligibility: !this.isReuseMovement ? this.dependentAlterationSessionStorage.eligibilityId ? Number(this.dependentAlterationSessionStorage.eligibilityId) : null : null,
          plan: !this.isReuseMovement ? this.dependentAlterationSessionStorage.planId ? Number(this.dependentAlterationSessionStorage.planId) : null : null,
          validityDate: !this.isReuseMovement ? dateDependentFormat || null : null,
          unitId: this.dependentAlterationSessionStorage.unitId || this.dependentAlterationSessionStorage.unit || null,
          unitCode: this.dependentAlterationSessionStorage.unitCode || null,
          unitDescription: this.dependentAlterationSessionStorage.unitDescription || null,
          units: [],
          unit: {
            id: this.dependentAlterationSessionStorage.unitId || this.dependentAlterationSessionStorage.unit || null,
            code: this.dependentAlterationSessionStorage.unitCode || null,
            description: this.dependentAlterationSessionStorage.unitDescription || null,
          },
          movementDeadlineConfig: null,
        };
        this.currentPlanInfo = lodash.cloneDeep(this.plan);
      }
    },
    async mapBeneficiaryMovementRecord() {
      this.movementRecord = JSON.parse(sessionStorage.getItem('movementRecord'));
      if (this.movementRecord) {
        const dateFormat = this.movementRecord.startDate ? this.formatter.formatDate(this.movementRecord.startDate) : null;
        this.plan = {
          financialGroupId: this.movementRecord.financialGroupId ? Number(this.movementRecord.financialGroupId) : null,
          contract: !this.isReuseMovement ? this.movementRecord.contractId ? Number(this.movementRecord.contractId) : null : null,
          subcontract: !this.isReuseMovement ? this.movementRecord.subContractId ? Number(this.movementRecord.subContractId) : null : null,
          eligibility: !this.isReuseMovement ? this.movementRecord.contractEligibilityId ? Number(this.movementRecord.contractEligibilityId) : null : null,
          plan: !this.isReuseMovement ? this.movementRecord.plan ? Number(this.movementRecord.plan) : null : null,
          validityDate: !this.isReuseMovement ? dateFormat || null : null,
          planChangeReasonCode: this.movementRecord.planChangeReasonCode,
          familyCode: this.movementRecord.familyCode,
          familyCodeSequential: this.movementRecord.familyCodeSequential,
          unitId: this.movementRecord.unitId || this.movementRecord.unit || null,
          unitCode: this.movementRecord.unitCode || null,
          unitDescription: this.movementRecord.unitDescription || null,
          units: [],
          unit: {
            id: this.movementRecord.unitId || this.movementRecord.unit || null,
            code: this.movementRecord.unitCode || null,
            description: this.movementRecord.unitDescription || null,
          },
          capacityCode: this.movementRecord.capacityCode,
          capacityDescription: this.movementRecord.capacityDescription,
          department: this.movementRecord.department,
          movementDeadlineConfig: null,
        };
        this.isDisabledFamilyCode = this.plan.familyCode !== null;
        this.isDisabledFamilyCodeSequential = this.plan.familyCodeSequential !== null;
        this.currentPlanInfo = lodash.cloneDeep(this.plan);
      }
    },
    async onClickEdit() {
      this.isDisabledFields = false;
      this.$emit('disabledPlanInformation', this.isDisabledFields);
    },
    async saveDataInSessionStorage(mountingForm) {
      if (this.$refs.form.validate()) {
        const alterationObject = JSON.parse(sessionStorage.getItem('alterationObject'));
        alterationObject.plan = this.plan;
        alterationObject.plan.previousPlan = this.currentPlanInfo.plan !== this.plan.plan ? this.currentPlanInfo.plan : null;
        alterationObject.previousSubContractNumber = this.currentPlanInfo.previousSubContractNumber !== this.subContractNumber ? this.currentPlanInfo.previousSubContractNumber : null;
        alterationObject.contract = this.plan.contract ? String(this.plan.contract) : null;
        alterationObject.subContractNumber = this.subContractNumber;
        alterationObject.subcontract = this.plan.subcontract ? String(this.plan.subcontract) : null;
        const validityDateClone = this.plan.validityDate ? lodash.cloneDeep(this.plan.validityDate) : null;
        alterationObject.plan.validityDate = this.plan.validityDate ? this.formatter.formatDateBRtoString(this.plan.validityDate) : null;
        if (this.isFreeMovement) {
          alterationObject.cmovementRecordontract = this.plan.contract;
          alterationObject.contractPolicy = this.currentPlanInfo.contractPolicy;
        }
        sessionStorage.setItem('alterationObject', JSON.stringify(alterationObject));
        this.plan.validityDate = lodash.cloneDeep(validityDateClone);
        if (!mountingForm) {
          this.isDisabledFields = true;
        }
        this.$emit('disabledPlanInformation', this.isDisabledFields);
      }
    },
    async saveMovementRecordPrevious() {
      if (sessionStorage.getItem('movementRecord')) {
        let alterationObject = JSON.parse(sessionStorage.getItem('alterationObject'));
        if (alterationObject.plan) alterationObject.plan.previousPlan = this.movementRecord.previousPlan || null;
        alterationObject.previousSubContractNumber = this.movementRecord.previousSubContract || null;
        alterationObject.previousSubContract = this.movementRecord.previousSubContract || null;
        sessionStorage.setItem('alterationObject', JSON.stringify(alterationObject));
      }
    },
    resetDates() {
          if(this.plan.validityDate && this.movementType == 'UPDATE') {
            this.plan.validityDate = null;
            this.clearScheduledReleaseDate();
          }
    },
    async loadSubContracts() {
      if (this.isReuseMovement) {
        this.plan.subcontract = null;
        this.plan.eligibility = null;
        this.plan.plan = null;
      }

      this.isDisabledSubContracts = true;

      await this.getSubContractsByContractId(this.plan.contract);
      if (this.isReuseMovement) {
        const beneficiarySubContractIds = await this.fetchBeneficiarySubContractIds(this.movementRecord);
        this.subContracts = this.subContracts.filter((sub) => beneficiarySubContractIds.includes(sub.id));
      }

      this.isDisabledSubContracts = false;
    },
    async loadEligibilities() {
      if (this.isReuseMovement) {
        this.plan.eligibility = null;
        this.plan.plan = null;
      }

      this.isDisabledEligibilites = true;

      if (!this.plan.subcontract) {
        this.plans = [];
        this.plan.plan = null;
      } else {
        await this.getEligibilitiesByContractAndSubcontracts(
          this.plan.contract,
          this.plan.subcontract,
          this.plan.eligibility
        );

        if (!this.isReuseMovement) {
          await this.loadPlans();
        } else {
          const beneficiaryEligibilityIds = await this.fetchBeneficiaryEligibityIds(this.movementRecord);
          this.eligibilities = this.eligibilities.filter((eligibility) => beneficiaryEligibilityIds.includes(eligibility.id));
        }
      }
      this.observableChangeSubContract();

      if (
        this.isReuseMovement &&
        !this.eligibilities ||
        (Array.isArray(this.eligibilities) && this.eligibilities.length === 0)) {
        await this.loadPlans();
      }

      this.isDisabledEligibilites = false;
    },
    async loadPlans() {
      if (this.isReuseMovement) {
        this.plan.plan = null;
      }

      this.plans = [];
      if (this.plan.eligibility) {
        const eligibilityFound = this.eligibilities.find((eligibility) => eligibility.id === this.plan.eligibility);
        await this.getPlansByEligibilityId(eligibilityFound);
        if (this.isReuseMovement) {
          const movementRecordSession = JSON.parse(sessionStorage.getItem('movementRecord'));
          const beneficiaryPlanIds = await this.fetchBeneficiaryPlans(movementRecordSession, this.plan.contract, this.plan.subcontract);
          this.plans = this.plans.filter((plan) => beneficiaryPlanIds.includes(plan.id));
        }
      } else if (this.plan.plan) {
        await this.getPlanByIdMixin(this.plan.plan);
      } else {
        if (!this.isReuseMovement) {
          await this.getPlansByAllEligibilities(this.eligibilitiesIds);
        }
      }

      if (!this.plan.eligibility && this.isReuseMovement) {
        const movementRecordSession = JSON.parse(sessionStorage.getItem('movementRecord'));
        const beneficiaryPlanIds = await this.fetchBeneficiaryPlans(movementRecordSession, this.plan.contract, this.plan.subcontract);
        this.plans = [];

        for (const planId of beneficiaryPlanIds) {
          await this.getPlanByIdMixin(planId);
        }
      }

      this.getCurrentPlanById();
    },
    async preparePlansForReuseByContractAndSubcontract() {
      const movementRecordSession = JSON.parse(sessionStorage.getItem('movementRecord'));
      const beneficiaryPlanIds = await this.fetchBeneficiaryPlans(movementRecordSession, this.plan.contract, this.plan.subcontract);
      this.plans = [];

      for (const planId of beneficiaryPlanIds) {
        await this.getPlanByIdMixin(planId);
      }
    },
    async validateAndAssignReusePlanFields() {
      this.loadingSubContracts = true;
      this.loadingEligibilities = true;
      this.loadingPlans = true;
      this.loadingValidityDate = true;

      try {
        const movementRecordSession = JSON.parse(sessionStorage.getItem('movementRecord'));
        const beneficiaryBenefits = await this.fetchBeneficiaryBenefit(movementRecordSession, this.plan.contract);

        if (Array.isArray(beneficiaryBenefits) && beneficiaryBenefits.length === 1) {
          this.subContracts = [];
          this.eligibilities = [];
          this.plans = [];

          const uniqueBenefit = beneficiaryBenefits[0];

          this.plan.subcontract = uniqueBenefit.subcontractId;
          this.plan.eligibility = uniqueBenefit.eligibilityId;
          this.plan.plan = uniqueBenefit.planId;
          this.plan.validityDate = uniqueBenefit.validityDate;

          await this.getSubContractsByContractId(this.plan.contract, "onlyMainFields=true");

          if (this.plan.eligibility) {
            await this.getEligibilitiesByContractAndSubcontracts(
              this.plan.contract,
              this.plan.subcontract,
              this.plan.eligibility
            );

            const eligibilityFound = this.eligibilities.find((eligibility) => eligibility.id === this.plan.eligibility);
            await this.getPlansByEligibilityId(eligibilityFound);
          } else {
            await this.preparePlansForReuseByContractAndSubcontract()
          }

          this.subContracts = this.subContracts.filter((sub) => beneficiaryBenefits.some((benefit) => benefit.subcontractId === sub.id));
          this.eligibilities = this.eligibilities.filter((eligibility) => beneficiaryBenefits.some((benefit) => benefit.eligibilityId === eligibility.id));
          this.plans = this.plans.filter((plan) => beneficiaryBenefits.some((benefit) => benefit.planId === plan.id));
        }
      } finally {
        this.loadingSubContracts = false;
        this.loadingEligibilities = false;
        this.loadingPlans = false;
        this.loadingValidityDate = false;
      }

      this.$emit("observerContractedPlan", this.plan)
    },
    async getCurrentPlanById() {
      if (this.isMounted) {
        if (this.currentPlanInfo.plan) {
          let planData = [];
          if (this.plans && this.plans.length > 0) {
            planData = this.plans;
            const planFound = planData.find((plan) => plan.id === planData[0].id);
            if (planFound) {
              this.planName = `${planFound.name}`;
              this.isMounted = false;
            }
          }
        }
      }
    },
    observableChangeSubContract() {
      if (this.subContracts && this.subContracts.length > 0) {
        const subContractFound = this.subContracts.find((element) => element.id === this.plan.subcontract);
        this.subContractNumber = subContractFound && subContractFound.subcontract_number ? subContractFound.subcontract_number : null;
      }
    },
    observableChangePlan() {
      this.previousPlanName = (this.currentPlanInfo.plan && this.plan.plan) && (this.currentPlanInfo.plan !== this.plan.plan) ? `Plano Atual: ${this.planName}` : null;
    },
    async loadSessionStorageForFreeMovement() {
      if (sessionStorage.getItem('planInfo')) {
        const planInfo = JSON.parse(sessionStorage.getItem('planInfo'));
        this.financialGroupData = planInfo.financialGroupSelected ? planInfo.financialGroupSelected : null;
        this.contractData = planInfo.contractSelected ? planInfo.contractSelected : null;
        this.plan.financialGroupId = this.financialGroupData.id ? this.financialGroupData.id : null;
        this.plan.contract = this.contractData.id ? this.contractData.id : null;
      } else if (sessionStorage.getItem('movementRecord')) {
        this.mapBeneficiaryMovementRecord();
      }
    },
    saveValidatyDate(validatyDate) {
      if (validatyDate && validatyDate.length === 10) {
        this.$emit('saveValidatyDate', validatyDate);
      }
    },
    loadPlanChangeReasons() {
      this.planChangeReasonService.FindAll().then((response) => {
        if (response && response.data.length > 0) {
          this.planChangeReasons = response.data;
          this.planChangeReasons.sort((a, b) => (a.description > b.description ? 1 : -1));
        }
      });
    },
    async loadingContractedPlan() {
      this.loadSessionStorage();
      this.getFinancialGroups();
      if (!this.isReuseMovement) {
        this.getContractById(this.plan.contract);
      } else {
        await this.getContractsByFinancialGroupId(this.plan.financialGroupId);
        const beneficiaryContractIds = await this.fetchBeneficiaryContractIds(this.movementRecord);
        this.contracts = this.contracts.filter((contract) => beneficiaryContractIds.includes(contract.id));
      }
      const movementNotAllowedParam = !this.isEdit && !this.isCriticizedCarrier ? "movementNotAllowed=false" : "";
      await this.getSubContractsByContractId(
        this.isReuseMovement ? Number(this.movementRecord.contractId) : this.plan.contract,
        movementNotAllowedParam
      );
      this.setCurrentSubContract();
      await this.loadEligibilities();
      await this.loadUnits();
      this.setCurrentEligibility();
      sessionStorage.setItem('contractedPlanCurrent', JSON.stringify(this.currentPlanInfo));
      this.saveDataInSessionStorage(this.isReuseMovement ? true : false);
      this.saveMovementRecordPrevious();
      this.setCurrentValidityDate();
      this.setDaysToReleaseAppointment();
    },
    setDaysToReleaseAppointment() {
      this.validateShowScheduledReleaseDate()
    },
    setCurrentSubContract() {
      if (!this.isReuseMovement) {
        const subContractFound = this.subContracts.find((element) => element.id === this.plan.subcontract);
        this.currentPlanInfo.previousSubContractNumber = subContractFound && subContractFound.subcontract_number ? subContractFound.subcontract_number : null;
        this.previousSubContractName = subContractFound ? (`Sub-Contrato Atual: ${subContractFound.subcontract_number ? subContractFound.subcontract_number : '-'} - ${subContractFound.contract_owner_id && subContractFound.contract_owner_id.legal_name ? subContractFound.contract_owner_id.legal_name : '-'}`) : null;
      } else {
        this.subContracts = [];
      }
    },
    setCurrentEligibility() {
      const eligibilityFound = this.eligibilities.find(
        (element) => element.id === this.plan.eligibility);

      const eligibilityName = eligibilityFound
        && eligibilityFound.elegibility_id
        && eligibilityFound.elegibility_id.name
          ? eligibilityFound.elegibility_id.name
          : null;

      const areDifferents = this.plan.eligibility !== this.currentPlanInfo.eligibility;

      this.currentPlanInfo.previousEligibility = eligibilityName;

      this.previousEligibilityName = areDifferents ? `Elegibilidade Atual: ${ eligibilityName || '-' }` : '';
    },
    setCurrentValidityDate() {
      this.currentPlanInfo.previousValidityDate = this.isReuseMovement
        ? this.formatter.formatDate(this.movementRecord.startDate)
        : this.plan.validityDate;

      this.previousValidityDate = `Inicio de Vigência Atual: ${ this.currentPlanInfo.previousValidityDate || '-' }`;
    },
    setPreviewsValidityDate() {
      this.plan.validityDate = this.currentPlanInfo.previousValidityDate;
      this.validateShowScheduledReleaseDate()
    },
    getMessageFromRequiredChangedField(key, previewValue) {
      if (!['', null, undefined].some((emptyValue) => emptyValue === this.plan[key])) {
        if (this.currentPlanInfo[key] !== this.plan[key]) {
          return previewValue;
        }
      }

      return null;
    },
    getMessageFromChangedField(key, previewValue) {
      if (this.currentPlanInfo[key] !== this.plan[key]) {
        return previewValue;
      }
    },
    async validateShowScheduledReleaseDate() {
      if (!this.plan || Object.keys(this.plan).length === 0 || !this.plan.contract) {
        if (this.plan) {
          this.setMovementDeadlineValue(this.plan, 'movementDeadlineConfig', null);
        }
        this.showScheduledReleaseDate = false;
        this.scheduledReleaseDate = null;
        this.changeLoadingSheduledReleaseDate(false);
        await this.syncMovementDeadlineView();

        return;
      }

      const contract = this.contracts.find((contract) => contract.id === this.plan.contract);
      const subcontract = this.subContracts.find((contract) => contract.id === this.plan.subcontract);
      const movementDeadlineConfig = contract
        ? await this.getMovementDeadlineConfigByContractId(
          subcontract.id,
          this.scheduledReleaseMovementType,
          contract.cut_off_date,
        )
        : null;
      this.setMovementDeadlineValue(this.plan, 'movementDeadlineConfig', movementDeadlineConfig);
      this.showScheduledReleaseDate = this.shouldShowMovementDeadlineField(
        this.plan.validityDate,
        this.plan.movementDeadlineConfig,
      );
      this.scheduledReleaseDate = null;
      this.changeLoadingSheduledReleaseDate(this.showScheduledReleaseDate);
      await this.syncMovementDeadlineView();

      if (this.showScheduledReleaseDate && this.plan.validityDate && contract) {
        try {
          const result = await this.getCalculateScheduledDate(
            this.plan.validityDate,
            subcontract.id,
            this.scheduledReleaseMovementType,
          );
          this.scheduledReleaseDate = result ? result : '-';
          await this.syncMovementDeadlineView();
        } catch (error) {
          this.$refs.SnackbarCustomize.open('error', 'Ocorreu um erro na busca pela data agendada');
        } finally {
          this.changeLoadingSheduledReleaseDate(false);
          await this.syncMovementDeadlineView();
        }
      }
    },
    async clearScheduledReleaseDate() {
      const contract = this.contracts.find((contract) => contract.id === this.plan.contract);
      const subcontract = this.subContracts.find((contract) => contract.id === this.plan.subcontract);
      const movementDeadlineConfig = contract
        ? await this.getMovementDeadlineConfigByContractId(
          subcontract.id,
          this.scheduledReleaseMovementType,
          contract.cut_off_date,
        )
        : null;
      this.setMovementDeadlineValue(this.plan, 'movementDeadlineConfig', movementDeadlineConfig);
      this.showScheduledReleaseDate = this.shouldShowMovementDeadlineField(
        this.plan.validityDate,
        this.plan.movementDeadlineConfig,
      );
      this.scheduledReleaseDate = null;
      this.changeLoadingSheduledReleaseDate(false);
      await this.syncMovementDeadlineView();
    },
    changeLoadingSheduledReleaseDate(value = null) {
      this.loadingScheduledReleaseDate = typeof value === 'boolean'
        ? value
        : !this.loadingScheduledReleaseDate;
    }
  },
  computed: {
    areDifferentValidityDates() {
      return !this.mountedFinish
        && (this.plan.validityDate !== this.currentPlanInfo.previousValidityDate);
    },
    subContractMessage() {
      return this.getMessageFromRequiredChangedField(
        'subcontract',
        this.previousSubContractName
      );
    },
    eligibilityMessage() {
      return this.getMessageFromChangedField(
        'eligibility',
        this.previousEligibilityName
      );
    },
    validityDateMessage() {
      return this.getMessageFromRequiredChangedField(
        'validityDate',
        this.previousValidityDate
      );
    },
    disabledPlanInput() {
      return (
        this.isDisabledFields ||
        this.mountedFinish ||
        this.loadingPlans ||
        this.isDisabledPlans ||
        this.isDisabledEligibilites
      ) ||
      (
        !this.plan.subcontract ||
        (
          this.plan.subcontract &&
          !this.plan.eligibility &&
          Array.isArray(this.eligibilities) &&
          this.eligibilities.length > 0
        )
      );
    },
    isDisabledUnitField() {
      return (
        this.isDisabledFields ||
        this.mountedFinish ||
        this.loadingUnits ||
        !this.plan.contract ||
        !this.plan.subcontract
      );
    },
  },
  async created() {
    this.formatter = new Formatters();
    this.rule = new Rules();
    this.planChangeReasonService = new PlanChangeReasonServiceSyncBase();
    this.carrierService = new CarrierService();
    this.contractUnitService = new ContractUnitService();

    const fullPath = this.$router.currentRoute.fullPath.toString();
    this.movementType = fullPath.match(/movementType=([^&]*)/)[1];
  },
};
</script>
